import type { TypedDocumentNode } from '@apollo/client'
import type * as GraphQLOperations from 'types/graphql'

import { gql } from '@apollo/client'

import {
  openApprovalIn,
  parsePendingApproval,
} from 'src/lib/agentChat/agentEvents'

import type {
  AgentCardData,
  AgentToolCall,
  GeneratedFileData,
  Message,
  PendingApproval,
  ThinkingTreeData,
  ToolCallEvent,
  ToolCallStatus,
  TurnStopReason,
} from './types'

export const ANALYTICS_CONVERSATIONS: TypedDocumentNode<
  GraphQLOperations.AnalyticsConversationsQuery,
  GraphQLOperations.AnalyticsConversationsQueryVariables
> = gql`
  query AnalyticsConversationsQuery($pm: String) {
    analyticsConversations(pm: $pm) {
      id
      title
      pm
      updatedAt
    }
  }
`

export const ANALYTICS_CONVERSATION_DETAIL: TypedDocumentNode<
  GraphQLOperations.AnalyticsConversationDetailQuery,
  GraphQLOperations.AnalyticsConversationDetailQueryVariables
> = gql`
  query AnalyticsConversationDetailQuery($id: String!) {
    analyticsConversation(id: $id) {
      id
      title
      pm
      messages {
        id
        role
        text
        thinking
        toolCalls
        cards
        files
        approvalRequests
        stopReason
      }
    }
  }
`

/** A persisted tool call: the legacy name/input/result triple plus the
 *  optional row fields newer turns record (see api PersistedToolCall). */
export type HydratedToolCall = AgentToolCall &
  Partial<Omit<ToolCallEvent, 'name'>>

interface DisplayMessage {
  id: string
  role: string
  text: string
  thinking?: ThinkingTreeData | null
  toolCalls?: HydratedToolCall[] | null
  cards?: AgentCardData[] | null
  files?: GeneratedFileData[] | null
  approvalRequests?: unknown[] | null
  stopReason?: TurnStopReason | null
}

const TOOL_STATUSES: readonly ToolCallStatus[] = [
  'running',
  'awaiting-approval',
  'succeeded',
  'failed',
  'rejected',
  'refused',
]

/** Tool rows from persisted tool calls. Only calls that recorded a callId
 *  become rows (legacy analytics rows stay CSV-export data only). */
const toToolEvents = (
  toolCalls: HydratedToolCall[],
  open: PendingApproval | null
): ToolCallEvent[] =>
  toolCalls.flatMap((call) => {
    if (typeof call.id !== 'string' || !call.id) return []
    const persisted =
      TOOL_STATUSES.find((s) => s === call.status) ?? 'succeeded'
    const stillOpen =
      open !== null && open.items.some((item) => item.callId === call.id)
    // A row left running/awaiting on a message that is no longer live never
    // finished — unless its approval card is still open and actionable.
    const status: ToolCallStatus =
      (persisted === 'running' || persisted === 'awaiting-approval') &&
      !stillOpen
        ? 'failed'
        : stillOpen
          ? 'awaiting-approval'
          : persisted
    return [
      {
        id: call.id,
        name: call.name,
        toolKey: call.toolKey ?? '',
        capability: call.capability ?? 'core',
        connector: call.connector ?? 'twodots',
        effect: call.effect === 'write' ? 'write' : 'read',
        status,
        title: call.title ?? call.name,
        argsPreview: call.argsPreview ?? '',
        startedAt: call.startedAt ?? '',
        ...(typeof call.durationMs === 'number'
          ? { durationMs: call.durationMs }
          : {}),
        ...(typeof call.resultPreview === 'string'
          ? { resultPreview: call.resultPreview }
          : {}),
      },
    ]
  })

/**
 * Persisted messages → the flat FE model. Every message is finalized except
 * the LAST one when it still carries an open approval: that turn is paused,
 * not finished, so the card stays actionable after a reload.
 */
export const toMessages = (
  messages: DisplayMessage[],
  now: number = Date.now()
): Message[] =>
  messages.map((message, index) => {
    if (message.role === 'user') {
      return { id: message.id, role: 'user', text: message.text }
    }
    const approvals = (message.approvalRequests ?? [])
      .map(parsePendingApproval)
      .filter((approval): approval is PendingApproval => approval !== null)
    const isLast = index === messages.length - 1
    const assistant: Extract<Message, { role: 'assistant' }> = {
      id: message.id,
      role: 'assistant',
      text: message.text,
      files: message.files ?? [],
      done: true,
      ...(message.thinking
        ? { thinking: { ...message.thinking, finalized: true } }
        : {}),
      ...(message.toolCalls ? { toolCalls: message.toolCalls } : {}),
      ...(message.cards ? { cards: message.cards } : {}),
      ...(approvals.length > 0 ? { approvals } : {}),
      ...(message.stopReason ? { stopReason: message.stopReason } : {}),
    }
    const open = isLast ? openApprovalIn(assistant, now) : null
    const toolEvents = message.toolCalls
      ? toToolEvents(message.toolCalls, open)
      : []
    return {
      ...assistant,
      ...(toolEvents.length > 0 ? { toolEvents } : {}),
      done: open === null,
    }
  })
